import { useEffect, useState } from 'react';
import { Eye, EyeOff, type LucideIcon } from '@/components/icons';

/** Soft shadow that keeps white KPI text legible on the lighter accents (amber, cyan, orange). */
export const KPI_TEXT_SHADOW = '0 1px 2px rgba(0,0,0,0.18)';

/** Remembered per page + card label in this browser, like the table column picker. */
const maskKey = (label: string) => `kpi.masked.${window.location.pathname}.${label}`;
function readMasked(label: string): boolean {
  try { return localStorage.getItem(maskKey(label)) === '1'; } catch { return false; }
}

/**
 * Canonical ERP KPI card — solid accent surface, faded white corner icon,
 * uppercase label, big white value, subtle hover lift. Matches the dashboard
 * tiles so every page's KPIs read as one system. The eye button hides the
 * value as "xxxxx" (screen only; remembered per page and card).
 */
export function KpiCard({
  label,
  value,
  accent,
  Icon,
  loading,
  size = 'lg',
}: {
  label: string;
  value: string | number | null | undefined;
  accent: string;
  Icon?: LucideIcon;
  loading?: boolean;
  size?: 'lg' | 'sm';
}) {
  const empty = value == null || value === '—';
  const [masked, setMasked] = useState(() => readMasked(label));
  useEffect(() => { setMasked(readMasked(label)); }, [label]);
  function toggleMask() {
    const next = !masked;
    setMasked(next);
    try {
      if (next) localStorage.setItem(maskKey(label), '1');
      else localStorage.removeItem(maskKey(label));
    } catch { /* storage blocked — the toggle still works until reload */ }
  }
  const MaskIcon = masked ? EyeOff : Eye;
  return (
    <div
      className="group relative overflow-hidden rounded-xl p-4 text-white shadow-sm transition-transform hover:-translate-y-0.5"
      style={{ background: accent, textShadow: KPI_TEXT_SHADOW }}
    >
      {Icon && (
        <Icon className="absolute -bottom-3 -right-3 h-20 w-20" style={{ color: '#fff', opacity: 0.18 }} strokeWidth={1.25} />
      )}
      <button
        type="button"
        onClick={toggleMask}
        title={masked ? 'Show value' : 'Hide value'}
        aria-label={masked ? `Show ${label}` : `Hide ${label}`}
        aria-pressed={masked}
        className={`absolute right-2 top-2 z-10 rounded-md p-1 text-white/80 transition hover:bg-white/20 hover:text-white focus-visible:opacity-100 ${masked ? 'opacity-100' : 'opacity-60 sm:opacity-0 sm:group-hover:opacity-100'}`}
      >
        <MaskIcon size={14} />
      </button>
      <div className="relative">
        <span className="pr-6 text-[10px] font-extrabold uppercase tracking-widest text-white/90">
          {label}
        </span>
        <div className={`mt-1.5 font-extrabold ${size === 'sm' ? 'text-sm' : 'text-2xl'} ${empty && !masked ? 'text-white/70' : ''}`}>
          {loading ? (
            <span className="inline-block h-6 w-16 animate-pulse rounded bg-white/30" />
          ) : masked ? (
            <span className="select-none tracking-widest">xxxxx</span>
          ) : (
            value ?? '—'
          )}
        </div>
      </div>
    </div>
  );
}
